import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, type PaginatedResult } from './api-client';

/** Security deposits: balances, the immutable ledger and the requirement (spec §8, §36). */

export type DepositStatus =
  | 'NOT_REQUIRED'
  | 'PENDING'
  | 'PAID'
  | 'PARTIALLY_PAID'
  | 'REFUNDED'
  | 'HELD'
  | 'FORFEITED';

export type DepositTransactionType =
  | 'PAYMENT'
  | 'REFUND'
  | 'HOLD'
  | 'RELEASE'
  | 'ADJUSTMENT'
  | 'FORFEIT';

export interface DepositBalances {
  requiredAmount: number;
  paidAmount: number;
  heldAmount: number;
  refundedAmount: number;
  forfeitedAmount: number;
  balanceAmount: number;
  outstandingAmount: number;
  contributedAmount: number;
  isSatisfied: boolean;
  status: DepositStatus;
  currency: string;
}

export interface DepositCustomer {
  id: string;
  customerCode: string;
  companyName: string;
  status: string;
  isVerified: boolean;
  canOrder: boolean;
}

export interface DepositRow {
  customer: DepositCustomer;
  deposit: DepositBalances | null;
  updatedAt: string;
}

export interface DepositLedgerRow {
  id: string;
  type: DepositTransactionType;
  amount: number;
  balanceAfter: number;
  reference: string | null;
  notes: string | null;
  createdAt: string;
  performedBy: { id: string; name: string } | null;
  orderId: string | null;
  paymentId: string | null;
}

export interface DepositDetail {
  customer: DepositCustomer & { contactEmail: string | null };
  deposit: DepositBalances | null;
  minimumAmount: number;
  transactions: DepositLedgerRow[];
}

export interface DepositSummary {
  pending: number;
  partiallyPaid: number;
  paid: number;
  held: number;
  refunded: number;
  forfeited: number;
  totalPaid: number;
  totalHeld: number;
  totalRefunded: number;
  totalBalance: number;
}

export interface DepositListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: DepositStatus[];
  outstandingOnly?: boolean;
}

const keys = {
  all: ['admin', 'deposits'] as const,
  list: (params: DepositListParams) => [...keys.all, 'list', params] as const,
  summary: () => [...keys.all, 'summary'] as const,
  detail: (customerId: string) => [...keys.all, 'detail', customerId] as const,
};

export function useDeposits(params: DepositListParams) {
  return useQuery({
    queryKey: keys.list(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<DepositRow>>('/admin/deposits', {
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          status: params.status?.length ? params.status.join(',') : undefined,
          // Sent only when on — `outstandingOnly=false` means "no filter".
          outstandingOnly: params.outstandingOnly || undefined,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useDepositSummary() {
  return useQuery({
    queryKey: keys.summary(),
    queryFn: () => apiClient.get<DepositSummary>('/admin/deposits/summary'),
  });
}

export function useDeposit(customerId: string | null) {
  return useQuery({
    queryKey: keys.detail(customerId ?? ''),
    queryFn: () => apiClient.get<DepositDetail>(`/admin/deposits/${customerId}`),
    enabled: Boolean(customerId),
  });
}

/**
 * Any deposit write invalidates the list, the counters, the detail *and* the
 * customer queries — settling or forfeiting a deposit moves an ordering gate,
 * which the customer screens display.
 */
function useDepositMutation<TVariables>(mutationFn: (variables: TVariables) => Promise<unknown>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.all });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
  });
}

export interface DepositEntryInput {
  customerId: string;
  amount: number;
  reference?: string;
  notes?: string;
}

export function useRecordPayment() {
  return useDepositMutation(({ customerId, ...body }: DepositEntryInput) =>
    apiClient.post(`/admin/deposits/${customerId}/payment`, body),
  );
}

export function useRecordRefund() {
  return useDepositMutation(({ customerId, ...body }: DepositEntryInput) =>
    apiClient.post(`/admin/deposits/${customerId}/refund`, body),
  );
}

export function useRecordForfeit() {
  return useDepositMutation(({ customerId, ...body }: DepositEntryInput) =>
    apiClient.post(`/admin/deposits/${customerId}/forfeit`, body),
  );
}

/** The one signed entry — a correction may legitimately run either way. */
export function useRecordAdjustment() {
  return useDepositMutation(
    ({
      customerId,
      ...body
    }: {
      customerId: string;
      amount: number;
      notes: string;
      reference?: string;
    }) => apiClient.post(`/admin/deposits/${customerId}/adjustment`, body),
  );
}

export function useSetRequirement() {
  return useDepositMutation(
    ({
      customerId,
      ...body
    }: {
      customerId: string;
      requiredAmount: number;
      reason?: string;
    }) => apiClient.patch(`/admin/deposits/${customerId}/requirement`, body),
  );
}

// --- Presentation helpers ----------------------------------------------------

export const DEPOSIT_STATUS_LABEL: Record<DepositStatus, string> = {
  NOT_REQUIRED: 'Not required',
  PENDING: 'Pending',
  PARTIALLY_PAID: 'Partially paid',
  PAID: 'Paid',
  HELD: 'Held',
  REFUNDED: 'Refunded',
  FORFEITED: 'Forfeited',
};

export const TRANSACTION_LABEL: Record<DepositTransactionType, string> = {
  PAYMENT: 'Payment',
  REFUND: 'Refund',
  HOLD: 'Held against order',
  RELEASE: 'Released',
  ADJUSTMENT: 'Adjustment',
  FORFEIT: 'Forfeited',
};
